import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckIcon, GiftIcon, LockIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { bonuses, modules, mockupUrl, purchaseUrl, savingsPercent, savingsValue, todayPriceValue, totalBonusValue, totalValue, usd, volumesValue } from '../data/content';
import { Countdown } from './ui/Countdown';
export function Pricing({
  id = 'pricing'
}: {
  id?: string;
}) {
  return <section id={id} className="scroll-mt-24 bg-white px-4 py-14 md:py-16" aria-labelledby={`${id}-title`} data-id="element-238" data-mp="f9q70vj3wl39">
      <div className="mx-auto max-w-4xl text-center" data-id="element-239" data-mp="6behyxxw1vcx">
        <p className="text-[11px] font-extrabold uppercase tracking-[.16em] text-red-600 md:text-sm" data-id="element-240" data-mp="6av0fic5h8zf">
          ⏰ Launch price — available now
        </p>
        <div className="mt-4" data-id="element-241" data-mp="2r0u4vhtzkzxa">
          <Countdown data-id="element-242" data-mp="2r0kali1ws6ls" />
        </div>

        <article className="mt-8 rounded-3xl border-2 border-amber-300 bg-white p-6 text-left shadow-2xl shadow-amber-200/60 ring-4 ring-amber-100 md:p-9" data-id="element-243" data-mp="2r3q2t65u3olq">
          <div className="text-center" data-id="element-244" data-mp="2fodspjr8egud">
            <h2 id={`${id}-title`} className="text-xl font-black uppercase text-blue-950 md:text-3xl" data-id="element-245" data-mp="2fpfd4eu50for">
              📊 Everything you get today
            </h2>
            <p className="mt-2 text-sm font-semibold text-slate-600 md:text-base" data-id="element-246" data-mp="2opu2bpa82exj">
              Access to all 5 complete volumes + 5 practical bonuses
            </p>
            <a href={`#${id}`} className="mt-5 flex justify-center" aria-label="CLEAR THE CLUTTER™ offer" data-id="element-247" data-mp="2ylx5mygt8jm2">
              <img src={mockupUrl} alt="CLEAR THE CLUTTER™ complete box set" className="w-full max-w-[280px] object-contain drop-shadow-xl" data-id="element-248" data-mp="2to591r5809bj" />
            </a>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-249" data-mp="33dylsvh3muhd">
            {/* Volumes — left */}
            <div className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50 p-5" data-id="element-250" data-mp="3hd7r303ro46k">
              <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-blue-950" data-id="element-251" data-mp="3cffx80jka7zq">
                <BookOpenIcon className="h-4 w-4 text-yellow-700" aria-hidden="true" data-id="element-252" data-mp="29ycfimww6lg6" />
                5 Complete Volumes
              </h3>
              <ul className="mt-4 flex-1 space-y-2.5" data-id="element-253" data-mp="2j0lpnbo90ct1">
                {modules.map(mod => <li key={mod.name} className="flex items-start gap-2 text-sm font-bold text-slate-700" data-id="element-254" data-mp="2nsdi5rqysslt">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" data-id="element-255" data-mp="2srq326y9zplr" />
                    <span data-id="element-256" data-mp="2xprmiszm5qxr">
                      <span className="text-yellow-700" data-id="element-257" data-mp="2xoypijqgifst">{mod.volume}</span> — {mod.name}
                    </span>
                  </li>)}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-sm" data-id="element-258" data-mp="36qg3nwkb07ev">
                <span className="font-black uppercase text-blue-950" data-id="element-259" data-mp="18bv5q2kme6q0">Volumes value</span>
                <span className="font-black text-blue-950" data-id="element-260" data-mp="1cc5ajbwoer3a">{usd(volumesValue)}</span>
              </div>
            </div>

            {/* Bonuses — right */}
            <div className="flex flex-col rounded-2xl border border-amber-200 bg-amber-50 p-5" data-id="element-261" data-mp="14v8sunjqch4h">
              <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-blue-950" data-id="element-262" data-mp="1014hls3flmxl">
                <GiftIcon className="h-4 w-4 text-yellow-700" aria-hidden="true" data-id="element-263" data-mp="v101i36fqu7w" />
                5 Practical Bonuses
              </h3>
              <ul className="mt-4 flex-1 space-y-2.5" data-id="element-264" data-mp="1ns9zsdwugrb2">
                {bonuses.map(bonus => <li key={bonus.number} className="flex items-start justify-between gap-3 text-sm font-bold text-slate-700" data-id="element-265" data-mp="1isp0219uoexa">
                    <span className="flex items-start gap-2" data-id="element-266" data-mp="19sinqipw1kzp">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" data-id="element-267" data-mp="19ud1nd2qscua" />
                      <span data-id="element-268" data-mp="3dhsygcln8up">
                        <span className="text-yellow-700" data-id="element-269" data-mp="3uihitrlu3zkw">Bonus {bonus.number}</span> — {bonus.name}
                      </span>
                    </span>
                    <span className="shrink-0 text-right" data-id="element-270" data-mp="1m900jyi662zm">
                      <s className="block text-xs font-black text-slate-400" data-id="element-271" data-mp="1r08biyz5dwew">{usd(bonus.value)}</s>
                      <span className="text-[11px] font-black uppercase text-emerald-700" data-id="element-272" data-mp="1d9d4ztc4freh">Free</span>
                    </span>
                  </li>)}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-amber-200 pt-3 text-sm" data-id="element-273" data-mp="2509jszhqs64e">
                <span className="font-black uppercase text-blue-950" data-id="element-274" data-mp="29z411x029mme">Bonuses value</span>
                <span className="font-black text-slate-400 line-through" data-id="element-275" data-mp="1vzl5s4scm5ri">{usd(totalBonusValue)}</span>
              </div>
            </div>
          </div>

          <dl className="mx-auto mt-6 max-w-sm space-y-2 text-sm" data-id="element-276" data-mp="20x4j92h82eie">
            <div className="flex items-center justify-between gap-3" data-id="element-277" data-mp="kk6ar6jp9nz1">
              <dt className="font-semibold text-slate-600" data-id="element-278" data-mp="pjivnlr0gkyz">5 Volumes</dt>
              <dd className="font-black text-slate-700" data-id="element-279" data-mp="pku0low6zyro">{usd(volumesValue)}</dd>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-280" data-mp="h0p253ucir4s">
              <dt className="font-semibold text-slate-600" data-id="element-281" data-mp="czvf0coq9gcy">+ 5 Bonuses</dt>
              <dd className="font-black text-slate-700" data-id="element-282" data-mp="r17tsks5jbx9">{usd(totalBonusValue)}</dd>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-2" data-id="element-283" data-mp="m13bqqbli94x">
              <dt className="font-black uppercase text-blue-950" data-id="element-284" data-mp="3ugved796keqf">Total value</dt>
              <dd className="text-lg font-black text-slate-400 line-through decoration-red-500 decoration-2" data-id="element-285" data-mp="3pibmyrbtiaqo">
                {usd(totalValue)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-286" data-mp="83wr0q7ba0d2">
              <dt className="font-black uppercase text-blue-950" data-id="element-287" data-mp="39iv8moqlr7v">You pay today</dt>
              <dd className="text-lg font-black text-blue-950" data-id="element-288" data-mp="3bpvjjpf08ap8">{usd(todayPriceValue)}</dd>
            </div>
          </dl>

          <p className="mt-5 text-center text-sm font-black uppercase text-emerald-700" data-id="element-289" data-mp="36raqc9y66raa">
            🎁 All 5 bonuses included free today
          </p>

          <div className="mt-6 text-center" data-id="element-290" data-mp="1fn8wjtopney7">
            <p className="inline-flex rounded-full bg-red-600 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white" data-id="element-291" data-mp="1fm7hzf8hkabq">
              You save {usd(savingsValue)} today · {savingsPercent}% off
            </p>
            <div className="mx-auto mt-6 max-w-xl" data-id="element-292" data-mp="1ohx27ky76ynj">
              <motion.a href={purchaseUrl} target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.012
            }} whileTap={{
              scale: 0.988
            }} transition={{
              duration: 0.14,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex w-full flex-col items-center gap-1 rounded-2xl border-b-4 border-amber-600 bg-amber-400 px-6 py-5 text-center shadow-xl shadow-amber-500/30 transition-colors duration-150 ease-out hover:bg-amber-300 focus:outline-none focus:ring-4 focus:ring-amber-400/50" data-id="element-293" data-mp="rqv3jksju1q3">
                <span className="text-[11px] font-extrabold uppercase tracking-[.16em] text-blue-950/80" data-id="element-294" data-mp="2r7n6tiag1q5d">
                  Today · Launch price
                </span>
                <span className="text-4xl font-black leading-none text-blue-950 md:text-5xl" data-id="element-295" data-mp="2hin4bowqgcmf">
                  {usd(todayPriceValue)}
                </span>
                <span className="mt-1 text-sm font-black uppercase tracking-wide text-blue-950 md:text-base" data-id="element-296" data-mp="2dh1ktkpp1039">
                  🏠 Get Clear the Clutter™ →
                </span>
              </motion.a>
            </div>
            <p className="mt-3 text-xs font-bold text-slate-500" data-id="element-297" data-mp="23lglxvfx8fxq">
              One purchase. Lifetime access.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-semibold text-slate-500" data-id="element-298" data-mp="1uixknfrsabdl">
              <span className="flex items-center gap-1" data-id="element-299" data-mp="3sxign47wq1zt">
                <LockIcon className="h-3.5 w-3.5 text-yellow-700" data-id="element-300" data-mp="3ovx81v2eo9vf" /> Secure payment
              </span>
              <span className="flex items-center gap-1" data-id="element-301" data-mp="bzapcxlc4g2n">
                <ZapIcon className="h-3.5 w-3.5 text-yellow-700" data-id="element-302" data-mp="c1x53kidq1vy" /> Digital access in minutes
              </span>
              <span className="flex items-center gap-1" data-id="element-303" data-mp="g38qfdu800yc">
                <ShieldCheckIcon className="h-3.5 w-3.5 text-yellow-700" data-id="element-304" data-mp="297xj0py985w" /> 7-day guarantee
              </span>
            </div>
          </div>
        </article>
      </div>
    </section>;
}